import type { ExerciseDef, PlanConfig, PlanInputs, RpeTable, SetDef, Track, Unit } from './types.ts';

/**
 * Plan interpreter (ADR-0020). Pure, and in the source sheet's own arithmetic: display units and MROUND to the
 * track increment (nearest; halves away from zero), so every plan can be proven against its spreadsheet's cached
 * values (test/plans.golden.test.ts). Callers convert to Mass at the edges.
 */

/** Google Sheets / Excel MROUND: nearest multiple of m, halves away from zero. Tolerant of float noise. */
export function mround(x: number, m: number): number {
  if (m === 0) return x;
  const q = x / m;
  const r = Math.sign(q) * Math.floor(Math.abs(q) + 0.5 + 1e-9);
  return +(r * m).toFixed(6);
}

export function rpePct(table: RpeTable, rpe: number, reps: number): number {
  const i = table.rpe.indexOf(rpe);
  const j = table.reps.indexOf(reps);
  if (i < 0 || j < 0) throw new RangeError(`RPE table has no entry for RPE ${rpe} × ${reps}`);
  return table.pct[i]![j]!;
}

/** Estimated 1RM from a set: weight ÷ table[rpe][reps] (the sheets' formula). */
export const e1rm = (table: RpeTable, weight: number, reps: number, rpe: number) => weight / rpePct(table, rpe, reps);

export interface TrackState {
  value: number;
  weekStart: number;
  increment: number;
  rampStep: number;
}
/** `session` counts finished sessions (weeks follow it); `pos` is the rotation position ("do B instead" moves it). */
export type PlanState = { session: number; pos: number; tracks: Record<string, TrackState> };

const incFor = (t: Track, unit: Unit, inputs: PlanInputs) => inputs[t.id]?.increment ?? t.increment[unit];

export function startWeight(t: Track, unit: Unit, inputs: PlanInputs, table: RpeTable): number {
  const inc = incFor(t, unit, inputs);
  const s = t.start;
  if (s.type === 'fixed') return inputs[t.id]?.weight ?? s.weight[unit]; // the sheets let you overwrite the start
  const inp = inputs[t.id];
  if (!inp) throw new Error(`Track ${t.id} needs a recent set (weight × reps @ RPE)`);
  const est = e1rm(table, inp.weight, inp.reps, inp.rpe);
  const onRamp = inp.onRamp ?? 4;
  const back = inc * onRamp * (s.perWeek ?? 1);
  if (typeof s.pct === 'number') return mround(est * s.pct - back, inc);
  const reps = s.pct.reps === 'target' ? (t.targetReps ?? 5) : s.pct.reps;
  const frac = rpePct(table, s.pct.rpe, reps);
  return s.roundAfter ? mround(est * frac - back, inc) : +(mround(est * frac, inc) - back).toFixed(6);
}

/**
 * Fill in inputs a plan derives from another lift (Intermediate variations, Ultra Max incline): the parent's
 * weight × pct, rounded, with the parent's reps and effort. Inputs the user gave are never overwritten.
 */
export function deriveInputs(plan: PlanConfig, unit: Unit, inputs: PlanInputs): PlanInputs {
  const out: PlanInputs = { ...inputs };
  for (const t of plan.tracks) {
    if (out[t.id] || !t.inputFrom) continue;
    const parent = out[t.inputFrom.track];
    if (!parent) continue;
    const { increment: _i, rampStep: _r, ...rest } = parent;
    out[t.id] = { ...rest, weight: mround(parent.weight * t.inputFrom.pct, t.inputFrom.roundTo[unit]) };
  }
  return out;
}

export function initPlan(plan: PlanConfig, unit: Unit, given: PlanInputs, table: RpeTable): PlanState {
  const inputs = deriveInputs(plan, unit, given);
  const tracks: Record<string, TrackState> = {};
  for (const t of plan.tracks) {
    const v = startWeight(t, unit, inputs, table);
    tracks[t.id] = { value: v, weekStart: v, increment: incFor(t, unit, inputs), rampStep: inputs[t.id]?.rampStep ?? t.rampStep ?? 0.125 };
  }
  return { session: 0, pos: 0, tracks };
}

export const workoutAt = (plan: PlanConfig, pos: number) => plan.rotation[pos % plan.rotation.length]!;
export const weekOf = (plan: PlanConfig, session: number) => Math.floor(session / plan.daysPerWeek) + 1;

export interface PrescribedSet {
  reps: number;
  weight: number;
  kind: NonNullable<SetDef['kind']>;
}
export interface PrescribedExercise {
  track: string;
  exercise: string;
  sets: PrescribedSet[];
}

export function setLoad(ts: TrackState, d: SetDef): number {
  const basis = d.basis === 'weekStart' ? ts.weekStart : ts.value;
  const raw = basis * (d.pct ?? 1) * (1 - (d.step ?? 0) * ts.rampStep);
  return d.pct === undefined && !d.step ? basis : mround(raw, ts.increment);
}

/** What the next session asks for (state at `session`). */
export function prescribe(plan: PlanConfig, st: PlanState, override?: string): { workout: string; week: number; exercises: PrescribedExercise[] } {
  const workout = override ?? workoutAt(plan, st.pos);
  const w = plan.workouts.find((x) => x.id === workout);
  if (!w) throw new Error(`Plan ${plan.id} has no workout ${workout}`);
  const exercises = w.exercises.map((e) => {
    const ts = st.tracks[e.track]!;
    const t = plan.tracks.find((x) => x.id === e.track)!;
    return { track: e.track, exercise: t.exercise, sets: e.sets.map((d) => ({ reps: d.reps, weight: setLoad(ts, d), kind: d.kind ?? 'work' })) };
  });
  return { workout, week: weekOf(plan, st.session), exercises };
}

function passed(t: Track, ex: ExerciseDef, logged: (number | null)[]): boolean {
  const idx = t.progress.test?.sets && t.progress.test.sets !== 'all' ? t.progress.test.sets : ex.sets.map((_, i) => i);
  const p = t.progress.pass;
  if (p.type === 'always') return true;
  return idx.every((i) => {
    const got = logged[i] ?? 0;
    if (p.type === 'minReps') return got >= p.min;
    return got >= ex.sets[i]!.reps; // allReps / targetReps
  });
}

function stepUp(t: Track, ts: TrackState, unit: Unit): number {
  for (const th of t.progress.thresholds ?? []) if (ts.value > th.above[unit]) return th.increment[unit];
  return ts.increment;
}

/**
 * Apply one finished session. `logged[e][s]` is the reps achieved on set s of exercise e (in the workout's order).
 * Tracks progress when their test passes (in the test workout, if one is named); weekly snapshots roll forward.
 */
export function finishSession(plan: PlanConfig, st: PlanState, unit: Unit, logged: (number | null)[][], done?: string): PlanState {
  const workout = done ?? workoutAt(plan, st.pos);
  const w = plan.workouts.find((x) => x.id === workout)!;
  const tracks: Record<string, TrackState> = Object.fromEntries(Object.entries(st.tracks).map(([k, v]) => [k, { ...v }]));
  const decided = new Set<string>();
  w.exercises.forEach((ex, i) => {
    const t = plan.tracks.find((x) => x.id === ex.track)!;
    if (t.progress.test?.workout && t.progress.test.workout !== workout) return;
    if (decided.has(t.id)) return; // a track tested twice in one workout progresses once
    decided.add(t.id);
    if (passed(t, ex, logged[i] ?? [])) {
      const ts = tracks[t.id]!;
      ts.value = +(ts.value + stepUp(t, st.tracks[t.id]!, unit)).toFixed(6);
    }
  });
  const next = st.session + 1;
  if (next % plan.daysPerWeek === 0) for (const ts of Object.values(tracks)) ts.weekStart = ts.value;
  const at = plan.rotation.indexOf(workout);
  return { session: next, pos: (at < 0 ? st.pos : at) + 1, tracks };
}

/** Run `weeks` of the plan, logging what `reps` says (default: every set hit). For projections and golden parity. */
export function simulate(plan: PlanConfig, unit: Unit, inputs: PlanInputs, table: RpeTable, weeks: number, reps?: (session: number, exercise: number, set: number, target: number) => number | null) {
  let st = initPlan(plan, unit, inputs, table);
  const out: ReturnType<typeof prescribe>[] = [];
  for (let s = 0; s < weeks * plan.daysPerWeek; s++) {
    const p = prescribe(plan, st);
    out.push(p);
    const logged = p.exercises.map((e, ei) => e.sets.map((x, si) => (reps ? reps(s, ei, si, x.reps) : x.reps)));
    st = finishSession(plan, st, unit, logged);
  }
  return out;
}
